import React, { useState, useEffect, useCallback } from 'react';
import {
  TicketIcon,
  ArrowPathIcon,
  UserIcon,
  PlusIcon,
  EyeIcon,
  ChevronUpDownIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import {
  TicketIcon as TicketIconSolid,
  LifebuoyIcon as LifebuoyIconSolid
} from '@heroicons/react/24/solid';

import axiosInstance from '../api/axiosInstance';
import BreadcrumbNavigation from './BreadcrumbNavigation';

// Design System Components
import Button from '../ui/Button';
import InputComponent, { Textarea } from '../ui/Input';
import { SearchInput } from '../ui/Input';
import Card, { CardHeader } from '../ui/Card';
import { StatusBadge } from '../ui/Table';
import SidePanel from '../ui/SidePanel';
import logger from '../../utils/logger';

// The help desk that actually owns tickets (threading, SLA, assignment, agent
// triage). Overridable per environment; the production host is the default.
const HELPDESK_URL = process.env.REACT_APP_HELPDESK_URL || 'https://helpdesk.aiinak.com';

const ModernSupport = ({ showNotification }) => {
  const [loading, setLoading] = useState(false);
  
  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketFilter, setTicketFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Modal state
  const [showNewTicketPanel, setShowNewTicketPanel] = useState(false);
  const [showTicketDetailPanel, setShowTicketDetailPanel] = useState(false);

  // Tickets created here now live in the helpdesk (helpdesk.aiinak.com), which
  // owns threading, SLA, assignment and agent triage. This screen still files a
  // ticket, but the list below reads admin-service's LEGACY local collection,
  // which no longer receives new tickets — so it is shown as an archive and the
  // helpdesk is linked as the place to actually work tickets.
  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/business/support/tickets');
      setTickets(response.data.tickets || []);
    } catch (error) {
      logger.error('Error fetching tickets:', error);
      // No mock data - set empty array
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Create new ticket
  const createTicket = async (ticketData) => {
    try {
      const response = await axiosInstance.post('/api/business/support/tickets', ticketData);
      
      if (response.data.success) {
        const ticketId = response.data.ticket?.ticketNumber;
        showNotification && showNotification(
          ticketId
            ? `Ticket ${ticketId} created — track it in the Help Desk.`
            : 'Support ticket created successfully!',
          'success'
        );
        setShowNewTicketPanel(false);
        setTicketForm({
          subject: '',
          description: '',
          priority: 'medium',
          category: 'general'
        });
        // Deliberately no fetchTickets() here: the ticket now lives in the
        // helpdesk, so refetching the legacy local list would show nothing and
        // read as though the ticket was lost.
      }
    } catch (error) {
      logger.error('Error creating support ticket:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create support ticket';
      showNotification && showNotification(errorMessage, 'error');
    }
  };

  const handleTicketSubmit = async () => {
    if (!ticketForm.subject.trim() || !ticketForm.description.trim()) {
      showNotification && showNotification('Please fill in all required fields', 'error');
      return;
    }
    await createTicket(ticketForm);
  };

  // Priority and status styling
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'success';
      default: return 'gray';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'primary';
      case 'in_progress': return 'warning';
      case 'waiting_for_customer': return 'secondary';
      case 'resolved': return 'success';
      case 'closed': return 'gray';
      default: return 'gray';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'urgent': return 'Urgent';
      case 'high': return 'High';
      case 'medium': return 'Medium';
      case 'low': return 'Low';
      default: return 'Unknown';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open': return 'Open';
      case 'in_progress': return 'In Progress';
      case 'waiting_for_customer': return 'Waiting';
      case 'resolved': return 'Resolved';
      case 'closed': return 'Closed';
      default: return 'Unknown';
    }
  };

  // Filter and sort tickets
  const filteredAndSortedTickets = tickets
    .filter(ticket => {
      const matchesFilter = ticketFilter === 'all' || ticket.status === ticketFilter;
      const matchesSearch = searchQuery === '' || 
        ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.ticketNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesFilter && matchesSearch;
    })
    .sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'createdBy') {
        aValue = typeof a.createdBy === 'object' ? a.createdBy.name || a.createdBy.email : a.createdBy;
        bValue = typeof b.createdBy === 'object' ? b.createdBy.name || b.createdBy.email : b.createdBy;
      }
      
      if (sortField === 'createdAt' || sortField === 'updatedAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDetailPanel(true);
  };

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'technical', label: 'Technical' },
    { value: 'account', label: 'Account' },
    { value: 'billing', label: 'Billing' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'bug_report', label: 'Bug Report' }
  ];

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const TableHeader = ({ field, children, sortable = true }) => (
    <th 
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
        sortable ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-[#222222] select-none' : ''
      }`}
      onClick={sortable ? () => handleSort(field) : undefined}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortable && (
          <ChevronUpDownIcon className={`w-4 h-4 ${sortField === field ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`} />
        )}
      </div>
    </th>
  );

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111] overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <LifebuoyIconSolid className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Support Center</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => window.open(HELPDESK_URL, '_blank', 'noopener,noreferrer')}
                icon={<LifebuoyIconSolid />}
              >
                Open Help Desk
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowNewTicketPanel(true)}
                icon={<PlusIcon />}
              >
                New Ticket
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={fetchTickets}
                loading={loading}
                icon={<ArrowPathIcon />}
              >
                Refresh
              </Button>
            </div>
          </div>

          <BreadcrumbNavigation />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {(
          <div className="h-full flex flex-col">
            {/* New tickets are filed into the help desk; the table below is the
                legacy local archive and no longer receives them. Saying so
                plainly avoids the list reading as "my ticket disappeared". */}
            <div className="flex-shrink-0 flex items-start gap-2 px-4 py-2 bg-orange-50 dark:bg-orange-950/30 border-b border-orange-200 dark:border-orange-900/50">
              <LifebuoyIconSolid className="w-4 h-4 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-orange-900 dark:text-orange-200">
                New tickets are handled in the{' '}
                <a
                  href={HELPDESK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline hover:no-underline"
                >
                  Help Desk
                </a>
                , where replies, assignment and SLA tracking live. The list below is an
                archive of earlier tickets and is no longer updated.
              </p>
            </div>
            {/* Toolbar */}
            <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1a1a1a] px-4 py-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <SearchInput
                      placeholder="Search tickets..."
                      onSearch={setSearchQuery}
                      size="sm"
                    />
                  </div>
                  
                  <div>
                    <select
                      value={ticketFilter}
                      onChange={(e) => setTicketFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
                    >
                      <option value="all">All Status</option>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="waiting_for_customer">Waiting</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {filteredAndSortedTickets.length} of {tickets.length} tickets
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <ArrowPathIcon className="w-8 h-8 text-orange-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">Loading tickets...</p>
                  </div>
                </div>
              ) : filteredAndSortedTickets.length === 0 ? (
                <div className="text-center py-8">
                  <TicketIconSolid className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No tickets found</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {searchQuery || ticketFilter !== 'all' 
                      ? 'Try adjusting your search or filter criteria'
                      : 'Create your first support ticket to get started'
                    }
                  </p>
                  {!searchQuery && ticketFilter === 'all' && (
                    <Button
                      variant="primary"
                      onClick={() => setShowNewTicketPanel(true)}
                      icon={<PlusIcon />}
                    >
                      Create First Ticket
                    </Button>
                  )}
                </div>
              ) : (
                <Card>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px]">
                      <thead className="bg-gray-50 dark:bg-[#222222]">
                        <tr>
                          <TableHeader field="ticketNumber">Ticket</TableHeader>
                          <TableHeader field="subject">Subject</TableHeader>
                          <TableHeader field="status">Status</TableHeader>
                          <TableHeader field="priority">Priority</TableHeader>
                          <TableHeader field="category">Category</TableHeader>
                          <TableHeader field="createdBy">Requester</TableHeader>
                          <TableHeader sortable={false}><span className="sr-only">Actions</span></TableHeader>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                        {filteredAndSortedTickets.map((ticket) => {
                          const requester = typeof ticket.createdBy === 'object'
                            ? (ticket.createdBy.name || ticket.createdBy.email || '—')
                            : (ticket.createdBy || '—');
                          const initials = String(requester).trim().charAt(0).toUpperCase() || '?';
                          return (
                          <tr
                            key={ticket.id}
                            className="group hover:bg-orange-50/40 dark:hover:bg-[#1e1e1e] cursor-pointer transition-colors"
                            onClick={() => handleTicketClick(ticket)}
                          >
                            <td className="px-4 py-3.5 align-top whitespace-nowrap">
                              <span className="text-sm font-mono font-semibold text-orange-600 dark:text-orange-400">
                                {ticket.ticketNumber || `#${ticket.id}`}
                              </span>
                            </td>
                            <td className="px-4 py-3.5">
                              <div className="max-w-[260px]">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {ticket.subject}
                                </p>
                                {ticket.description && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                    {ticket.description}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3.5 align-top">
                              <StatusBadge
                                status={getStatusLabel(ticket.status)}
                                color={getStatusColor(ticket.status)}
                              />
                            </td>
                            <td className="px-4 py-3.5 align-top">
                              <StatusBadge
                                status={getPriorityLabel(ticket.priority)}
                                color={getPriorityColor(ticket.priority)}
                              />
                            </td>
                            <td className="px-4 py-3.5 align-top">
                              <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                                {String(ticket.category || 'general').replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3.5 align-top">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                  {initials}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm text-gray-900 dark:text-white truncate max-w-[160px]">{requester}</p>
                                  <p className="text-xs text-gray-400 dark:text-gray-500">
                                    {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3.5 align-top text-right whitespace-nowrap">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleTicketClick(ticket); }}
                                icon={<EyeIcon />}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>

      {/* New Ticket Side Panel */}
      <SidePanel
        isOpen={showNewTicketPanel}
        onClose={() => setShowNewTicketPanel(false)}
        title={
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <TicketIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">Create New Ticket</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Submit a new support request</div>
            </div>
          </div>
        }
        size="xl"
      >
        <div className="p-6 space-y-6">
          <Card>
            <CardHeader>
              <h3 className="font-semibold text-gray-900 dark:text-white">Ticket Details</h3>
            </CardHeader>
            
            <div className="space-y-4">
              <InputComponent
                label="Subject"
                placeholder="Brief description of your issue"
                value={ticketForm.subject}
                onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})}
                required
              />

              <Textarea
                label="Description"
                placeholder="Detailed description of your issue"
                value={ticketForm.description}
                onChange={(e) => setTicketForm({...ticketForm, description: e.target.value})}
                rows={4}
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                  <select
                    value={ticketForm.priority}
                    onChange={(e) => setTicketForm({...ticketForm, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 dark:bg-[#2a2a2a] dark:text-white rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
                  >
                    {priorities.map(priority => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                  <select
                    value={ticketForm.category}
                    onChange={(e) => setTicketForm({...ticketForm, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 dark:bg-[#2a2a2a] dark:text-white rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </Card>

          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              onClick={handleTicketSubmit}
              icon={<CheckCircleIcon />}
            >
              Create Ticket
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowNewTicketPanel(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </SidePanel>

      {/* Ticket Detail Side Panel */}
      <SidePanel
        isOpen={showTicketDetailPanel}
        onClose={() => {
          setShowTicketDetailPanel(false);
          setSelectedTicket(null);
        }}
        title={
          selectedTicket && (
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <TicketIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">{selectedTicket.subject}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">{selectedTicket.ticketNumber}</div>
              </div>
            </div>
          )
        }
        size="4xl"
      >
        {selectedTicket && (
          <div className="flex flex-col h-full">
            {/* Ticket Status Bar */}
            <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#222222] px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <StatusBadge 
                    status={getStatusLabel(selectedTicket.status)} 
                    color={getStatusColor(selectedTicket.status)}
                  />
                  <StatusBadge 
                    status={getPriorityLabel(selectedTicket.priority)} 
                    color={getPriorityColor(selectedTicket.priority)}
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                    {selectedTicket.category.replace('_', ' ')}
                  </span>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Created {new Date(selectedTicket.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* Description */}
                <Card>
                  <CardHeader>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Description</h3>
                  </CardHeader>
                  <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedTicket.description}</p>
                  </div>
                </Card>

                {/* Comments */}
                <Card>
                  <CardHeader>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Activity ({selectedTicket.comments?.length || 0})
                    </h3>
                  </CardHeader>
                  
                  <div className="space-y-4">
                    {selectedTicket.comments && selectedTicket.comments.length > 0 ? (
                      selectedTicket.comments.map((comment) => (
                        <div key={comment.id} className="flex space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <UserIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="bg-white dark:bg-[#222222] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {comment.author.name}
                                  </span>
                                  <StatusBadge 
                                    status={comment.author.role === 'admin' ? 'Support' : 'Customer'} 
                                    color={comment.author.role === 'admin' ? 'primary' : 'secondary'}
                                  />
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(comment.createdAt).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-gray-700 dark:text-gray-300">{comment.content}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-8">No comments yet.</p>
                    )}
                  </div>
                </Card>

                {/* Ticket Details */}
                <Card>
                  <CardHeader>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Ticket Details</h3>
                  </CardHeader>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created by</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                        {typeof selectedTicket.createdBy === 'object' 
                          ? selectedTicket.createdBy.name || selectedTicket.createdBy.email 
                          : selectedTicket.createdBy}
                      </dd>
                    </div>
                    
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last updated</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                        {new Date(selectedTicket.updatedAt).toLocaleString()}
                      </dd>
                    </div>
                    
                    {selectedTicket.assignedTo && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Assigned to</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                          {typeof selectedTicket.assignedTo === 'object' 
                            ? selectedTicket.assignedTo.name || selectedTicket.assignedTo.email 
                            : selectedTicket.assignedTo}
                        </dd>
                      </div>
                    )}
                    
                    {selectedTicket.slaDeadline && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">SLA deadline</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                          {new Date(selectedTicket.slaDeadline).toLocaleString()}
                        </dd>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}
      </SidePanel>
    </div>
  );
};

export default ModernSupport;